import { type ReactNode } from 'react';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Typography } from '@mui/material';

// An annotation, from the Annotations Figma file: a titled bar over a paragraph.
//
// Chrome, not product. It says something to whoever is looking at a prototype —
// what this screen is for, what is fake, what still has to be decided — and it
// exists as a component so those sentences look the same everywhere instead of
// each screen inventing a caption.
//
// The title bar is the whole treatment: a coloured strip with the word on it,
// then the note on the surface below. No icon and no shadow, because it is not a
// status and not a card raised above the page.
//
// Colour carries no meaning. The Figma file gives three and titles all three
// "Note", so it is a way of telling two annotations apart on one screen rather
// than a severity — which is why the prop is `colour` and not `kind`.

// Header fill and the text that sits on it, per colour. Held here rather than at
// the call site so a note cannot be built with the wrong text colour on cherry,
// which is the one combination that fails legibility.
//
// Cherry and green are the Figma file's first two. Its third is purple, and that
// is the retired lavender — deleted from the colour collection and a type error
// in this package — so sunflower stands in for it. On-system, still obviously a
// third thing at a glance, and the file's purple should follow the retirement.
//
// Sunflower is the pale end of its scale, not the brand yellow. It is the note
// that carries timings and explanations, so there are a dozen of them, and at
// 500 a page of them was the brightest thing on it.
const HEADER = {
  green: { bg: 'custom.limeGreen.500', text: 'text.primary' },
  cherry: { bg: 'custom.cherry.500', text: 'common.white' },
  sunflower: { bg: 'custom.sunflower.100', text: 'text.primary' },
} as const;

export type NoteColour = keyof typeof HEADER;

const Note = ({
  title = 'Note',
  colour = 'green',
  variant = 'body1',
  tone,
  children,
}: {
  /** The bar. Defaults to Note, which is what the Figma component ships with. */
  title?: string;
  colour?: NoteColour;
  /**
   * The note's own size. body1 is a caption on a screen; a long note, like a
   * readme heading a flow, reads better at body2.
   */
  variant?: 'body1' | 'body2';
  /**
   * Exact colours instead of one of the three, for a note that has to match
   * something else on the page rather than just be told apart from another
   * note.
   *
   * The plan page is the case: a stage is a bar with a fill, a border and
   * sometimes a dash, and the note about it takes the same three, so a filled
   * coral bar gets a filled coral note and a dashed one gets a dashed note.
   * Reading them from one place is what stops a colour meaning two things.
   */
  tone?: { head: string; frame: string; dashed?: boolean };
  children: ReactNode;
}) => (
  // No width. The Figma component is 500px because a Figma component has to be
  // some width; in here the page that places a note decides, the same way every
  // other layout in this app is decided by the thing doing the placing.
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: 1,
      borderStyle: tone?.dashed ? 'dashed' : 'solid',
      borderColor: tone ? tone.frame : 'divider',
      borderRadius: `${radius.sm}px`,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        bgcolor: tone ? tone.head : HEADER[colour].bg,
        borderBottom: 1,
        // Bottom only. `borderStyle` sets the style on all four sides, and a
        // side with a style and no width falls back to CSS's medium: it drew a
        // 3px box round the header rather than a rule under it.
        borderBottomStyle: tone?.dashed ? 'dashed' : 'solid',
        borderColor: tone ? tone.frame : 'divider',
        px: 2,
        py: 1,
      }}
    >
      <Typography
        sx={{ color: tone ? 'text.primary' : HEADER[colour].text }}
        variant='subtitle1'
      >
        {title}
      </Typography>
    </Box>

    {/* component='div' so a note can hold a list or two paragraphs without
        nesting block elements inside a <p>. A bare string still gets body1,
        which is the common case and the one worth not making callers wrap. */}
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{ color: 'text.primary' }}
        variant={variant}
        component='div'
      >
        {children}
      </Typography>
    </Box>
  </Box>
);

Note.displayName = 'Note';
export default Note;
